package com.neurobs.js.aws

import com.neurobs.aws.CognitoIdentityData
import com.neurobs.ccab.IdentityProvider.AccessData
import com.neurobs.ccab.{IdentityData, IdentityProvider}
import com.neurobs.errors.{FutureResult, Result, errorResult, tryResult}
import com.neurobs.util.JsonPrinter.jsonPrint
import org.scalajs.dom.{console, window}

import scala.concurrent.{Await, ExecutionContext, Future}
import scala.scalajs.js
import scala.scalajs.js.annotation._
import scala.scalajs.js.Dynamic.literal
import scala.concurrent.duration._
import scala.concurrent.ExecutionContext.Implicits.global

@js.native
@JSImport("jwt-decode", "jwtDecode")
object jwtDecode extends js.Function {
	def apply(token: String): js.Dynamic = js.native
}

object Cognito {
	import Amplify._

	case class Config
	(
		region: String,
		clientId: String,
		userPoolId: String
	)
	def configure(c: Config): Unit = {
		Amplify.configure(
			literal(
				Auth = literal(
					Cognito = literal(
						region = c.region,
						userPoolId = c.userPoolId,
						userPoolClientId = c.clientId,
						loginWith = literal(
							email = true
						)
					)
				)
			)
		)
	}

	case class AuthData
	(
		accessToken: String,
		idToken: String,
		//refreshToken: String,
		user: IdentityData
	)
	def currentUser(forceRefresh: Boolean): Future[Option[AuthData]] = {
		AmplifyAuth.fetchAuthSession(FetchAuthSessionOptions(forceRefresh)).toFuture map { session =>
			for {
				tokens <- session.tokens.toOption
				idToken <- tokens.idToken.toOption
				user <- decodeIdToken(idToken.toString)
			} yield AuthData(
				accessToken = tokens.accessToken.toString,
				idToken = idToken.toString,
				user = user
			)
		}
	}

	def signOut(): Future[Unit] = {
		AmplifyAuth.signOut().toFuture
	}

	def subscribeToChange(callback: () => Unit): () => Unit = {
		// signedIn and signedOut are dispatched after Amplify has updated its token
		// storage, so the callback can rely on the new state. tokenRefresh_failure
		// clears the tokens (a silent sign-out) only for non-network errors such as a
		// rejected refresh token; a network error keeps the tokens, so it is not a
		// sign-out and is ignored here.
		Hub.listen(channel = "auth", callback = arg => {
			arg.payload.event.toString match {
				case "signedIn" | "signedOut" =>
					callback()
				case "tokenRefresh_failure" =>
					val isNetworkError =
						arg.payload.data.error.name.toString == "NetworkError"
					if (!isNetworkError) callback()
				case _ => // tokenRefresh and others don't change login state
			}
		})
	}

	private def decodeIdToken(idToken: String): Option[IdentityData] = {
		try {
			val jwt = jwtDecode(idToken)
			com.neurobs.aws.CognitoIdentityData.toIdentityData { name: String =>
				val member = jwt.selectDynamic(name)
				if (name == CognitoIdentityData.ATTRIBUTE_NAME_GROUPS) {
					val arrValue = if (js.isUndefined(member)) Array[String]() else member.asInstanceOf[js.Array[String]].toArray
					Some(jsonPrint(arrValue))
				}
				else {
					if (js.isUndefined(member)) None
					else Some(member.toString)
				}
			}
		}
		catch {
			case _: Throwable => None
		}
	}
}

/*
@js.native
@JSImport("@aws-sdk/client-cognito-identity-provider", "CognitoIdentityProviderClient")
class CognitoIdentityProviderClient(config: js.Object) extends js.Object {
  def send(command: js.Object): js.Promise[js.Object] = js.native
}

@js.native
@JSImport("@aws-sdk/client-cognito-identity-provider", "InitiateAuthCommand")
class InitiateAuthCommand(input: js.Object) extends js.Object

@js.native
@JSImport("@aws-sdk/client-cognito-identity-provider", "NotAuthorizedException")
class NotAuthorizedException extends js.Error
*/
/*
@js.native
trait AuthenticationResultType extends js.Object {
	val AccessToken: js.UndefOr[String] = js.native
	val ExpiresIn: js.UndefOr[Double] = js.native
	val IdToken: js.UndefOr[String] = js.native
	val RefreshToken: js.UndefOr[String] = js.native
	val TokenType: js.UndefOr[String] = js.native
}

@js.native
trait InitiateAuthCommandOutput extends js.Object {
	val AuthenticationResult: js.UndefOr[AuthenticationResultType] = js.native
	val ChallengeName: js.UndefOr[String] = js.native
	val ChallengeParameters: js.UndefOr[js.Dictionary[String]] = js.native
	val Session: js.UndefOr[String] = js.native
}

case class CognitoResourceData
(
	region: String,
	clientId: String,
	userPoolId: String
)

class CognitoChallenge(output: InitiateAuthCommandOutput) extends AuthenticationChallenge {
	override def description: String = output.ChallengeName.get match {
		case "WEB_AUTHN" =>
		case "PASSWORD" =>
		case "PASSWORD_SRP" =>
		case "SELECT_CHALLENGE" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
		case "WEB_AUTHN" =>
	}
}

class Cognito(resources: CognitoResourceData)(implicit executionContext: ExecutionContext) extends IdentityProvider {

	private val client = new CognitoIdentityProviderClient(literal(region = resources.region))

	override def createUser(data: IdentityData, password: Option[String] = None): FutureResult[IdentityData] = throw new RuntimeException("Cognito.createUser not implemented.")

	override def setPassword(email: String, password: String): FutureResult[Unit] = throw new RuntimeException("Cognito.setPassword not implemented.")

	override def resendNewUserMessage(email: String): FutureResult[Unit] = throw new RuntimeException("Cognito.resendNewUserMessage not implemented.")

	override def decodeIdToken(idToken: String): Result[Option[IdentityData]] = {
		tryResult {
			val jwt = jwtDecode(idToken)
			com.neurobs.aws.CognitoIdentityData.toIdentityData { name: String =>
				val member = jwt.selectDynamic(name)
				if (js.isUndefined(member)) None
				else Some(member.toString)
			}
		}
	}

	override def authenticateUser(email: String, password: String): FutureResult[AuthenticateResult] = {
		val input = literal(
			AuthFlow = "USER_PASSWORD_AUTH",
			ClientId = resources.clientId,
			AuthParameters = literal(
				USERNAME = email,
				PASSWORD = password
			)
		)
		client.send(new InitiateAuthCommand(input)).toFuture map { response =>
			val output = response.asInstanceOf[InitiateAuthCommandOutput]
			if (output.AuthenticationResult.isDefined) {
				val auth = output.AuthenticationResult.get
				val temp = for {
					accessToken <- auth.AccessToken.toOption
					idToken <- auth.IdToken.toOption
					refreshToken <- auth.RefreshToken.toOption
					data <- decodeIdToken(idToken) match { case Left(_) => None; case Right(u) => u }
				} yield AuthenticateResultSuccess(
					data = data,
					accessData = AccessData(accessToken = accessToken, refreshToken = refreshToken, idToken = idToken)
				)
				temp match {
					case Some(d) => Right(d)
					case None => errorResult("The user data could not be validated.")
				}
			}
			else Right(IdentityProvider.TestAuthenticateResult(response.toString): AuthenticateResult)
		} recover {
			case e: js.JavaScriptException if (e.exception.isInstanceOf[NotAuthorizedException]) =>
				Right(AuthenticateResultFailed)
			case e: Throwable => errorResult(s"Error validating your information: ${e.getMessage}")
		}
	}

	override def respondToChallenge(challenge: AuthenticationChallenge, response: String): FutureResult[AuthenticateResult] = throw new RuntimeException("Cognito.respondToChallenge not implemented.")
}
*/